import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { en, TranslationKey, Dict } from "./en";
import { hy } from "./hy";
import { ru } from "./ru";

export type Lang = "en" | "hy" | "ru";

export const LANGS: { code: Lang; label: string; locale: string }[] = [
  { code: "en", label: "EN", locale: "en-US" },
  { code: "hy", label: "ՀԱՅ", locale: "hy-AM" },
  { code: "ru", label: "RU", locale: "ru-RU" },
];

const STORAGE_KEY = "touchtalk_lang";
const dicts: Record<Lang, Dict> = { en, hy, ru };

type Vars = Record<string, string | number>;

/** Pure lookup: English fallback for empty strings, `{name}` interpolation. */
export const translate = (lang: Lang, key: TranslationKey, vars?: Vars): string => {
  let text = dicts[lang][key] || en[key] || key;
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.split(`{${name}}`).join(String(value));
    }
  }
  return text;
};

export const localeOf = (lang: Lang) => LANGS.find((l) => l.code === lang)?.locale ?? "en-US";

const detect = (): Lang => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "hy" || stored === "ru") return stored;
  } catch {
    /* storage blocked — fall through to the browser language */
  }
  const nav = (typeof navigator !== "undefined" ? navigator.language : "") || "";
  if (/^hy/i.test(nav)) return "hy";
  if (/^ru/i.test(nav)) return "ru";
  return "en";
};

// Module-level mirror so non-React code (react-md validation callbacks)
// can read the active language without a hook.
let currentLang: Lang = detect();
export const getCurrentLang = () => currentLang;
/** t() usable outside components (always reflects the active language). */
export const tStatic = (key: TranslationKey, vars?: Vars) => translate(currentLang, key, vars);

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey, vars?: Vars) => string;
}

const I18nContext = createContext<I18nValue>({
  lang: currentLang,
  setLang: () => {},
  t: (key, vars) => translate(currentLang, key, vars),
});

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(currentLang);

  useEffect(() => {
    currentLang = lang;
    document.documentElement.lang = lang;
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  }, [lang]);

  const setLang = useCallback((next: Lang) => setLangState(next), []);
  const t = useCallback((key: TranslationKey, vars?: Vars) => translate(lang, key, vars), [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useT = () => useContext(I18nContext).t;
export const useLang = () => {
  const { lang, setLang } = useContext(I18nContext);
  return { lang, setLang, locale: localeOf(lang) };
};

export type { TranslationKey };
